// src/components/templates/TemplateDetail.jsx
//
// One template, in full: what replaces the grid when an author clicks Preview
// on a card. The prototype's detail view — the cover, the summary and what is
// included, five figures, a strip of the sample's pages, the structure and the
// format's specifications.
//
// **The pages are real.** The strip and "Preview Sample" show the template's
// sample manuscript laid out by the editor's own pagination (useSamplePages),
// and open it in the app's Book Preview — the same overlay the editor's
// Preview button opens — rather than a viewer of the gallery's own. So the
// sample shows what the app makes today: headings and ordinary paragraphs.
// Where that is not the format's industry layout yet (scripts, comics, papers,
// planning sheets), the "In the preview" note says so.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/Check';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import ViewQuiltOutlinedIcon from '@mui/icons-material/ViewQuiltOutlined';

import TemplateCover from './TemplateCover';
import useSamplePages from './useSamplePages';
import { primaryButtonSx } from './TemplateCard';
import { SP, FONT, BP, BP_WIDE, FONT_DISPLAY, CATEGORY_LABEL } from './templateTokens';
import BookPreviewModal from '../editor/modals/BookPreviewModal';
import { tokens } from '../../theme';

const THUMB_H = 264;

// What the editor does not lay out yet, by format, for the "In the preview" note.
const UNBUILT_FORMAT = {
  screenplay: 'screenplay',
  stageplay: 'stage play',
  comic: 'comic script',
  'academic-mla': 'MLA paper',
  'academic-apa': 'APA paper',
  'academic-chicago': 'Chicago paper',
  sheet: 'planning sheet',
};

export function previewNote(format) {
  const base = 'The sample is set up the way the app sets up this template: its sections as headings, with sample writing under them.';
  const unbuilt = UNBUILT_FORMAT[format];
  return unbuilt
    ? `${base} The editor doesn’t lay out ${unbuilt} pages in their industry format yet, so the writing is shown as plain text.`
    : base;
}

/** The first chapter heading on a page, for its caption. */
function headingOn(pageHtml) {
  const el = document.createElement('div');
  el.innerHTML = pageHtml;
  return el.querySelector('[data-heading-level="1"]')?.textContent.trim() || '';
}

const cardSx = {
  background: SP.white,
  border: `1px solid ${SP.lineSoft}`,
  borderRadius: '16px',
  p: '28px',
  mb: '20px',
  [BP.phone]: { p: '18px' },
};

const secHeadSx = { display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'baseline', columnGap: '12px', rowGap: '4px', mb: '18px' };
const secTitleSx = { m: 0, fontSize: 19, fontWeight: 700, letterSpacing: '-.01em', color: SP.black };
const hintSx = { fontSize: 12.5, color: SP.gray };

function Metric({ icon, label, value, sub }) {
  return (
    <Box
      sx={{
        px: '14px',
        borderLeft: `1px solid ${SP.lavender}`,
        '&:first-of-type': { borderLeft: 'none', pl: 0 },
        [BP.panelFloats]: { borderLeft: 'none', p: 0 },
      }}
    >
      <Box sx={{ fontSize: 12, color: SP.gray, display: 'flex', alignItems: 'center', gap: '6px', mb: '6px', '& svg': { fontSize: 14 } }}>
        {icon}{label}
      </Box>
      <Box sx={{ fontSize: 15, fontWeight: 700, color: SP.black }}>{value}</Box>
      {sub && <Box sx={{ fontSize: 11.5, color: SP.gray, mt: '2px' }}>{sub}</Box>}
    </Box>
  );
}

function PageThumb({ html, width, height, caption, sub, onOpen }) {
  const scale = THUMB_H / height;
  return (
    <Box component="button" type="button" onClick={onOpen} sx={thumbSx} aria-label={`Open ${caption}`}>
      <Box className="tpl-thumb-frame" sx={{ ...thumbFrameSx, width: Math.round(width * scale), height: THUMB_H, background: SP.white }}>
        <Box
          className="editor-main-content"
          style={{ '--rv-page-width': `${width}px`, '--rv-page-height': `${height}px` }}
          sx={{
            width,
            height,
            transform: `scale(${scale})`,
            transformOrigin: '0 0',
            pointerEvents: 'none',
            '& .rv-page': { m: 0, borderRadius: 0, boxShadow: 'none' },
          }}
          dangerouslySetInnerHTML={{ __html: html }}
        />
      </Box>
      <Box sx={thumbCapSx}>{caption}</Box>
      <Box sx={thumbSubSx}>{sub}</Box>
    </Box>
  );
}

const thumbSx = {
  flexShrink: 0,
  cursor: 'zoom-in',
  background: 'none',
  border: 'none',
  p: 0,
  textAlign: 'left',
  fontFamily: FONT.sans,
  '&:hover .tpl-thumb-frame': {
    boxShadow: `0 0 0 2px ${SP.purple}, 0 12px 22px -8px ${alpha(SP.purple, 0.35)}`,
    transform: 'translateY(-2px)',
  },
  '&:focus-visible': { outline: `2px solid ${SP.purpleMid}`, outlineOffset: '4px' },
};
const thumbFrameSx = {
  borderRadius: '4px',
  overflow: 'hidden',
  position: 'relative',
  boxShadow: `0 0 0 1px ${SP.lineSoft}, 0 6px 14px -6px ${alpha(SP.purpleDeep, 0.18)}`,
  transition: `all ${tokens.motion.base}ms ${tokens.motion.ease}`,
};
const thumbCapSx = { fontSize: 12, fontWeight: 600, color: SP.black, mt: '10px' };
const thumbSubSx = { fontSize: 11, color: SP.gray, mt: '1px' };

const roundButtonSx = (side) => ({
  position: 'absolute',
  top: 118,
  [side]: -19,
  width: 38,
  height: 38,
  borderRadius: '50%',
  background: SP.white,
  border: `1px solid ${SP.lineSoft}`,
  display: 'grid',
  placeItems: 'center',
  boxShadow: tokens.shadow.sm,
  zIndex: 2,
  cursor: 'pointer',
  color: SP.black,
  '&:hover': { borderColor: SP.purple, color: SP.purple },
  [BP.phone]: { [side]: -8 },
});

export default function TemplateDetail({ template, onBack, onUse }) {
  const { pages, measure, width, height } = useSamplePages(template);
  const [preview, setPreview] = useState(null); // null closed; { page } open
  const trackRef = useRef(null);
  const [dots, setDots] = useState({ n: 1, active: 0 });

  const captions = useMemo(() => {
    let last = '';
    return (pages || []).map((html) => {
      const heading = headingOn(html);
      if (heading) last = heading;
      return heading || (last ? `${last} (continued)` : '');
    });
  }, [pages]);

  // The dots under the strip: one per screenful, the current one wider.
  useEffect(() => {
    const tr = trackRef.current;
    if (!tr) return undefined;
    let timer;
    const update = () => {
      const n = Math.max(1, Math.ceil(tr.scrollWidth / Math.max(1, tr.clientWidth)));
      const active = Math.min(n - 1, Math.round((tr.scrollLeft / Math.max(1, tr.scrollWidth - tr.clientWidth)) * (n - 1)));
      setDots({ n, active: Number.isFinite(active) ? active : 0 });
    };
    const onScroll = () => { clearTimeout(timer); timer = setTimeout(update, 60); };
    update();
    tr.addEventListener('scroll', onScroll);
    window.addEventListener('resize', update);
    return () => { clearTimeout(timer); tr.removeEventListener('scroll', onScroll); window.removeEventListener('resize', update); };
  }, [pages]);

  const scrollTrack = (d) => {
    const tr = trackRef.current;
    tr?.scrollBy({ left: d * (tr.clientWidth - 120), behavior: 'smooth' });
  };
  const dotTo = (i) => {
    const tr = trackRef.current;
    if (!tr) return;
    tr.scrollTo({ left: ((tr.scrollWidth - tr.clientWidth) * i) / (dots.n - 1 || 1), behavior: 'smooth' });
  };

  const sections = template.sections || [];
  const maxWeight = Math.max(1, ...sections.map((s) => Number(s.weight) || 0));
  const specs = template.specs || [];
  const isScript = template.category === 'screenwriting' || template.category === 'stageplay';
  const pageCount = 1 + (pages?.length || 0);

  return (
    <Box data-testid="template-detail" sx={{ animation: 'tplFade .25s ease', '@keyframes tplFade': { from: { opacity: 0, transform: 'translateY(6px)' }, to: { opacity: 1, transform: 'none' } } }}>
      {measure}

      <Box
        component="button"
        type="button"
        onClick={onBack}
        sx={{
          display: 'inline-flex', alignItems: 'center', gap: '6px', color: SP.gray, fontSize: 13, fontWeight: 500,
          mb: '18px', cursor: 'pointer', background: 'none', border: 'none', p: 0, fontFamily: FONT.sans,
          '&:hover': { color: SP.purple }, '& svg': { fontSize: 16 },
        }}
      >
        <ArrowBackIcon />Back to Templates
      </Box>

      {/* ---------- head ---------- */}
      <Box sx={cardSx}>
        <Box
          sx={{
            display: 'grid', gridTemplateColumns: '200px 1fr 300px', gap: '32px', alignItems: 'start',
            [BP_WIDE]: { gridTemplateColumns: '190px 1fr' },
            [BP.panelFloats]: { gridTemplateColumns: '1fr' },
          }}
        >
          <Box component="button" type="button" onClick={() => setPreview({ page: null })} aria-label="View cover" sx={{ p: 0, border: 'none', background: 'none', cursor: 'zoom-in', width: 190 }}>
            <TemplateCover id={template.id} name={template.name} sx={{ width: 190, height: 285 }} />
          </Box>

          <Box>
            <Box component="h2" sx={{ m: 0, mb: '12px', fontFamily: FONT_DISPLAY, fontSize: 32, fontWeight: 600, letterSpacing: '-.01em', color: SP.black, lineHeight: 1.2 }}>
              {template.name}
            </Box>
            {template.tags?.length > 0 && (
              <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap', mb: '16px' }}>
                {template.tags.map((tag) => (
                  <Box key={tag} component="span" sx={{ fontSize: 12, background: SP.lavender, color: SP.purple, px: '10px', py: '4px', borderRadius: '6px', fontWeight: 500 }}>
                    {tag}
                  </Box>
                ))}
              </Box>
            )}
            <Box component="p" sx={{ m: 0, mb: '22px', fontSize: 14, color: SP.gray, lineHeight: 1.65, maxWidth: 560 }}>
              {template.summary || template.description}
            </Box>
            <Box sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <Box component="button" type="button" data-testid="detail-use" onClick={onUse} sx={{ ...primaryButtonSx, px: '22px', py: '11px', fontSize: 14, borderRadius: '9px' }}>
                Use This Template
              </Box>
              <Box
                component="button"
                type="button"
                data-testid="preview-sample"
                disabled={!pages?.length}
                onClick={() => setPreview({ page: 0 })}
                sx={{
                  ...primaryButtonSx, px: '22px', py: '11px', fontSize: 14, borderRadius: '9px',
                  background: SP.white, color: SP.purple, border: `1px solid ${SP.purpleSoft}`,
                  '&:hover': { background: SP.lavender },
                  '&:disabled': { opacity: 0.6, cursor: 'progress' },
                }}
              >
                Preview Sample
              </Box>
            </Box>
          </Box>

          {template.included?.length > 0 && (
            <Box sx={{ background: SP.lavender, border: `1px solid ${SP.lineSoft}`, borderRadius: '12px', p: '20px', [BP_WIDE]: { gridColumn: '1 / -1' } }}>
              <Box component="h3" sx={{ m: 0, mb: '14px', fontSize: 15, fontWeight: 700, color: SP.black }}>What’s Included</Box>
              {template.included.map((item) => (
                <Box key={item} sx={{ display: 'flex', gap: '10px', alignItems: 'flex-start', fontSize: 13, color: SP.black, mb: '11px', lineHeight: 1.35 }}>
                  <CheckIcon sx={{ fontSize: 16, color: SP.purple, flexShrink: 0, mt: '1px' }} />
                  <span>{item}</span>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        <Box
          sx={{
            display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', borderTop: `1px solid ${SP.lavender}`, mt: '26px', pt: '20px',
            [BP_WIDE]: { gridTemplateColumns: 'repeat(3, 1fr)', rowGap: '16px' },
            [BP.panelFloats]: { gridTemplateColumns: 'repeat(2, 1fr)' },
          }}
        >
          <Metric icon={<MenuBookOutlinedIcon />} label={template.unitLabel || 'Sections'} value={template.units || sections.length} sub={`${sections.length} structural sections`} />
          <Metric icon={<EditOutlinedIcon />} label="Word Count Goal" value={template.words} sub={isScript ? 'approx. total' : 'target range'} />
          <Metric icon={<DescriptionOutlinedIcon />} label="Length" value={template.length} sub={template.lengthNote} />
          <Metric icon={<PublicOutlinedIcon />} label="Genre" value={CATEGORY_LABEL[template.category] || template.category} sub={template.tags?.[1] || ''} />
          <Metric icon={<ViewQuiltOutlinedIcon />} label="Layout" value={template.layout} sub={specs[0]?.[1]} />
        </Box>
      </Box>

      {/* ---------- page strip ---------- */}
      <Box sx={cardSx}>
        <Box sx={secHeadSx}>
          <Box component="h3" sx={secTitleSx}>Template Preview</Box>
          <Box component="span" sx={hintSx}>
            {!pages ? 'Laying out the sample…' : pages.length ? `${pageCount} pages · click any page to read it full size` : 'The sample could not be laid out.'}
          </Box>
        </Box>
        <Box sx={{ position: 'relative' }}>
          {/* Only when there is somewhere to scroll to. */}
          {dots.n > 1 && <Box component="button" type="button" aria-label="Scroll left" onClick={() => scrollTrack(-1)} sx={roundButtonSx('left')}><ChevronLeftIcon fontSize="small" /></Box>}
          <Box
            ref={trackRef}
            data-testid="sample-track"
            sx={{
              display: 'flex', gap: '18px', overflowX: 'auto', scrollBehavior: 'smooth', p: '6px 4px 14px',
              scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            <Box component="button" type="button" onClick={() => setPreview({ page: null })} sx={thumbSx} aria-label="Open the cover">
              <TemplateCover className="tpl-thumb-frame" id={template.id} name={template.name} sx={{ width: 176, height: THUMB_H, transition: thumbFrameSx.transition }} />
              <Box sx={thumbCapSx}>Cover</Box>
              <Box sx={thumbSubSx}>{template.layout}</Box>
            </Box>
            {(pages || []).map((html, i) => (
              <PageThumb
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                html={html}
                width={width}
                height={height}
                caption={`Page ${i + 1}`}
                sub={captions[i]}
                onOpen={() => setPreview({ page: i })}
              />
            ))}
          </Box>
          {dots.n > 1 && <Box component="button" type="button" aria-label="Scroll right" onClick={() => scrollTrack(1)} sx={roundButtonSx('right')}><ChevronRightIcon fontSize="small" /></Box>}
        </Box>
        {dots.n > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: '6px', mt: '8px' }}>
            {Array.from({ length: dots.n }, (_, i) => (
              <Box
                key={i}
                component="button"
                type="button"
                aria-label={`Page group ${i + 1}`}
                onClick={() => dotTo(i)}
                sx={{
                  width: i === dots.active ? 24 : 8, height: 8, borderRadius: '4px', border: 'none', p: 0, cursor: 'pointer',
                  background: i === dots.active ? SP.purple : SP.lineSoft, transition: `all ${tokens.motion.base}ms`,
                }}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* ---------- structure & specs ---------- */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1.35fr 1fr', gap: '20px', [BP_WIDE]: { gridTemplateColumns: '1fr' } }}>
        <Box sx={{ ...cardSx, mb: 0 }}>
          <Box sx={secHeadSx}>
            <Box component="h3" sx={secTitleSx}>Structure Breakdown</Box>
            <Box component="span" sx={hintSx}>{sections.length} sections · fully customizable</Box>
          </Box>
          {sections.map((s, i) => (
            <Box
              key={`${i}-${s.title}`}
              sx={{
                display: 'grid', gridTemplateColumns: '28px 1fr auto', gap: '12px', alignItems: 'start', py: '12px',
                borderTop: `1px solid ${SP.lavender}`,
              }}
            >
              <Box sx={{ width: 24, height: 24, borderRadius: '50%', background: SP.lavender, color: SP.purple, fontSize: 11.5, fontWeight: 700, display: 'grid', placeItems: 'center' }}>
                {i + 1}
              </Box>
              <Box>
                <Box sx={{ fontSize: 13.5, fontWeight: 600, color: SP.black }}>{s.title}</Box>
                {s.prompt && <Box sx={{ fontSize: 12.5, color: SP.gray, mt: '2px', lineHeight: 1.4 }}>{s.prompt}</Box>}
                {Number(s.weight) > 0 && (
                  <Box sx={{ height: 4, background: SP.lavender, borderRadius: '2px', mt: '8px', overflow: 'hidden' }}>
                    <Box sx={{ height: '100%', width: `${Math.max(4, (Number(s.weight) * 100) / maxWeight)}%`, background: `linear-gradient(90deg, ${SP.purpleMid}, ${SP.purple})`, borderRadius: '2px' }} />
                  </Box>
                )}
              </Box>
              <Box sx={{ fontSize: 12, fontWeight: 600, color: SP.black, whiteSpace: 'nowrap', textAlign: 'right' }}>
                {s.target}
                {/^[\d,]+$/.test(s.target || '') && <Box sx={{ fontWeight: 400, color: SP.gray, fontSize: 11 }}>words</Box>}
              </Box>
            </Box>
          ))}
        </Box>

        <Box sx={{ ...cardSx, mb: 0 }}>
          <Box sx={secHeadSx}>
            <Box component="h3" sx={secTitleSx}>Format Specifications</Box>
            {template.category !== 'planning' && <Box component="span" sx={hintSx}>The standard this format follows</Box>}
          </Box>
          {specs.map(([label, value]) => (
            <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', gap: '16px', py: '11px', borderTop: `1px solid ${SP.lavender}`, fontSize: 13 }}>
              <Box component="span" sx={{ color: SP.gray }}>{label}</Box>
              <Box component="span" sx={{ fontWeight: 600, textAlign: 'right', color: SP.black }}>{value}</Box>
            </Box>
          ))}
          <Box sx={{ mt: '18px', background: SP.lavender, border: `1px solid ${SP.lineSoft}`, borderRadius: '10px', p: '14px 16px', fontSize: 12.5, color: SP.gray, lineHeight: 1.5 }}>
            <Box component="b" sx={{ color: SP.purpleDeep }}>In the preview:</Box> {previewNote(template.format)}
          </Box>
        </Box>
      </Box>

      {/* Mounted only while open, so each opening starts on the page asked for. */}
      {preview && (
        <BookPreviewModal
          open
          onClose={() => setPreview(null)}
          html={(pages || []).join('')}
          title={`${template.name} — Book Preview`}
          initialPage={preview.page}
        />
      )}
    </Box>
  );
}
